import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { getGuestbookEntries, addGuestbookEntry, deleteGuestbookEntry } from '../services/apiService';
import '../styles/guestbook.css';

// --- Tip Tanımları ---
interface GuestbookEntry {
  _id: string;
  author: string;
  message: string;
  createdAt: string;
  audioUrl?: string;
  audioDuration?: number;
}

interface GuestbookProps {
  weddingId: string | null;
  isOwner: boolean;
  weddingInfo?: { name?: string };
  isOpen: boolean;
  onClose: () => void;
}

const ENTRIES_PER_PAGE = 3;
const TypedHTMLFlipBook = HTMLFlipBook as any;

// --- Özel Şık Ses Çalar Bileşeni ---
const AudioPlayer: React.FC<{ url: string; duration?: number }> = ({ url, duration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(url);
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [url]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation(); // Kitap sayfası çevrilmesini engelle
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch((err) => console.error("Ses oynatılamadı:", err));
      setIsPlaying(true);
    }
  };

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Kitap sayfası çevrilmesini engelle
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const clickPercentage = clickX / width;
    const newTime = clickPercentage * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(clickPercentage * 100);
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div className="custom-audio-player" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={togglePlay} className="audio-play-btn">
        {isPlaying ? '⏸️' : '▶️'}
      </button>
      <div className="audio-timeline" onClick={handleTimelineClick}>
        <div className="audio-progress-bar" style={{ width: `${progress}%` }}></div>
      </div>
      <span className="audio-time">
        {formatTime(currentTime)} / {duration ? formatTime(duration) : formatTime(audioRef.current?.duration || 0)}
      </span>
    </div>
  );
};

const Guestbook: React.FC<GuestbookProps> = ({ weddingId, isOwner, weddingInfo, isOpen, onClose }) => {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const bookRef = useRef<any>(null);

  const [formData, setFormData] = useState({ author: '', message: '' });
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // --- Ses Kayıt State'leri ---
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<any>(null);

  const fetchEntries = useCallback(async () => {
    if (!weddingId) return;
    try {
      setLoading(true);
      const data: GuestbookEntry[] = await getGuestbookEntries(weddingId, isOwner);
      setEntries(data);
    } catch (err) {
      console.error("Anı defteri yüklenemedi:", err);
    } finally {
      setLoading(false);
    }
  }, [weddingId, isOwner]);

  useEffect(() => {
    if (isOpen) {
      fetchEntries();
    }
  }, [isOpen, fetchEntries]);

  const pages = useMemo((): GuestbookEntry[][] => {
    const chunkedEntries: GuestbookEntry[][] = [];
    for (let i = 0; i < entries.length; i += ENTRIES_PER_PAGE) {
      chunkedEntries.push(entries.slice(i, i + ENTRIES_PER_PAGE));
    }
    return chunkedEntries;
  }, [entries]);

  const handleDelete = async (e: React.MouseEvent, entryId: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (isOwner && window.confirm('Bu anıyı silmek istediğinizden emin misiniz?')) {
      try {
        await deleteGuestbookEntry(entryId);
        await fetchEntries();
      } catch (err) {
        alert('Silme sırasında bir hata oluştu.');
        console.error(err);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Ses Kayıt Fonksiyonları ---
  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      } catch (e) {
        // Safari ve bazı mobil tarayıcılar webm desteklemeyebilir, varsayılana düşür
        recorder = new MediaRecorder(stream);
      }
      
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordedBlob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        setAudioBlob(recordedBlob);
        const url = URL.createObjectURL(recordedBlob);
        setAudioUrl(url);

        // Mikrofonu kapat
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.start(250);
      setIsRecording(true);
      setRecordingDuration(0);

      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev >= 59) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Mikrofon erişim hatası:", err);
      setError("Mikrofona erişilemedi. Lütfen mikrofon izinlerinizi kontrol edin.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingDuration(0);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.author.trim()) {
      setError('Lütfen isminizi yazın.');
      return;
    }
    if (!formData.message.trim() && !audioBlob) {
      setError('Lütfen bir dilek yazın veya ses kaydı bırakın.');
      return;
    }
    if (!weddingId) {
      setError('Düğün ID bulunamadı.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      const submissionData = new FormData();
      submissionData.append('author', formData.author.trim());
      submissionData.append('message', formData.message.trim());
      
      if (audioBlob) {
        const mime = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const ext = mime.includes('mp4') ? 'mp4' : mime.includes('wav') ? 'wav' : 'webm';
        submissionData.append('audio', audioBlob, `voice_note.${ext}`);
        submissionData.append('audioDuration', String(recordingDuration));
      }

      await addGuestbookEntry(weddingId, submissionData);
      
      setFormData({ author: '', message: '' });
      deleteRecording();
      onClose(); // Formu gönderince modal'ı kapat
      await fetchEntries();
    } catch (err: any) {
      setError(err.message || 'Mesajınız gönderilirken bir hata oluştu.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  // --- DÜĞÜN SAHİBİ GÖRÜNÜMÜ ---
  if (isOwner) {
    return (
      <div className="modal-overlay book-modal-overlay" onClick={onClose}>
        <div className="modal-content book-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close-btn book-modal-close-btn" onClick={onClose}>&times;</button>

          <div className="book-container">
            {loading ? (
              <p style={{ color: 'white', fontSize: '1.2rem' }}>Anı defteri yükleniyor...</p>
            ) : (
              <>
                <TypedHTMLFlipBook
                  key={entries.length}
                  width={400} height={600} size="stretch"
                  minWidth={300} maxWidth={1000} minHeight={400} maxHeight={1350}
                  maxShadowOpacity={0.5} showCover={true} mobileScrollSupport={true}
                  usePortrait={true}
                  className="guestbook-album" ref={bookRef}
                >
                  <div className="book-page cover-page" data-density="hard">
                    <div className="page-content">
                      <h2>Anı Defteri</h2>
                      <p>{weddingInfo?.name || 'Bizim Hikayemiz'}</p>
                    </div>
                  </div>

                  {pages.map((pageEntries, pageIndex) => (
                    <div className="book-page" key={pageIndex}>
                      <div className="page-content">
                        {pageEntries.map((entry) => (
                          <div key={entry._id} className="entry-card-book">
                            {entry.message && <p className="entry-message-book">"{entry.message}"</p>}
                            {entry.audioUrl && <AudioPlayer url={entry.audioUrl} duration={entry.audioDuration} />}
                            <div className="entry-footer-book">
                              <span className="entry-author-book">- {entry.author}</span>
                              <button onClick={(e) => handleDelete(e, entry._id)} className="delete-entry-btn-book" title="Bu anıyı sil">Sil</button>
                            </div>
                          </div>
                        ))}
                        <div className="page-number">{pageIndex + 1}</div>
                      </div>
                    </div>
                  ))}

                  <div className="book-page cover-page back-cover" data-density="hard">
                    <div className="page-content">
                      <h2>Son.</h2>
                    </div>
                  </div>
                </TypedHTMLFlipBook>

                {!loading && entries.length > 0 && (
                  <div className="book-controls">
                    <button onClick={() => bookRef.current?.pageFlip().flipPrev()}>&lt; Önceki</button>
                    <button onClick={() => bookRef.current?.pageFlip().flipNext()}>Sonraki &gt;</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- MİSAFİR GÖRÜNÜMÜ ---
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>&times;</button>
        <div className="guestbook-form-card">
          <h3 className="section-title">📝 Anı Defteri</h3>
          <p style={{ textAlign: 'center', color: '#999', fontSize: '0.85rem', marginBottom: '1rem', marginTop: '-0.25rem' }}>Bir anı bırakın, bu özel günü daha da güzelleştirin.</p>
          <form onSubmit={handleSubmit}>
            <input
              type="text"
              name="author"
              placeholder="Adınız"
              value={formData.author}
              onChange={handleInputChange}
              required
              maxLength={50}
              disabled={isSubmitting}
            />
            <textarea
              name="message"
              placeholder="Dilekleriniz..."
              value={formData.message}
              onChange={handleInputChange}
              required={!audioBlob}
              rows={4}
              maxLength={500}
              disabled={isSubmitting}
            />

            {/* Ses Kayıt Alanı */}
            <div className="voice-record-section">
              {!audioUrl ? (
                <>
                  {isRecording ? (
                    <div className="recording-indicator">
                      <div className="recording-dot"></div>
                      <span>Kayıt Yapılıyor... {recordingDuration}s (Maks. 60s)</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: '#7b626c' }}>Dilerseniz sesli dilek bırakın:</span>
                  )}
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`record-btn ${isRecording ? 'recording' : 'idle'}`}
                    disabled={isSubmitting}
                  >
                    {isRecording ? '⏹️ Kaydı Durdur' : '🎙️ Ses Kaydet'}
                  </button>
                </>
              ) : (
                <div className="audio-preview-card">
                  <div className="audio-preview-header">
                    <span>🎙️ Ses Kaydınız Önizleme:</span>
                    <button type="button" onClick={deleteRecording} className="delete-record-btn" disabled={isSubmitting}>Sil ve Yeniden Başlat</button>
                  </div>
                  <audio src={audioUrl} controls style={{ width: '100%', height: '40px', marginTop: '4px' }} />
                </div>
              )}
            </div>

            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="upload-button" disabled={isSubmitting || isRecording}>
              {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Guestbook;